import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

const API_BASE = 'https://data.ahyc.group';
const API_DATA = '/items/zizhi'; 

interface DirectusZizhi {
  id: number;
  name: string;
  no: string | null;
  class: string[] | null;
  tag: string[] | null;
  image: string;
}

function App() {
  const [data, setData] = useState<DirectusZizhi[]>([]);
  const [categories, setCategories] = useState<string[]>(["全部"]);
  const [activeCat, setActiveCat] = useState("全部");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [mobileViewState, setMobileViewState] = useState<'nav' | 'list' | 'detail'>('nav');
  
  // LightBox State
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    const isLocal = window.location.hostname === 'localhost';
    const fetchUrl = isLocal ? API_DATA : `${API_BASE}${API_DATA}`;

    fetch(fetchUrl)
      .then(res => res.json())
      .then(json => {
        const items = json.data as DirectusZizhi[];
        const sortedItems = items.sort((a, b) => a.id - b.id);
        setData(sortedItems);
        const uniqueClasses = Array.from(new Set(sortedItems.map(item => item.class || []).flat())).filter(Boolean);
        setCategories(["全部", ...uniqueClasses]);
        setLoading(false);
        if (sortedItems.length > 0 && window.innerWidth > 768) {
          setSelectedId(sortedItems[0].id);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  // Handle ESC key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsZoomed(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSelectCategory = (cat: string) => {
    setActiveCat(cat);
    setSelectedId(null);
    setMobileViewState('list');
  };

  const handleSelectItem = (id: number) => {
    setSelectedId(id);
    setMobileViewState('detail');
  };

  const filtered = data.filter(d => {
    const query = search.toLowerCase().trim();
    if (query) {
      return d.name.toLowerCase().includes(query) || (d.no && d.no.toLowerCase().includes(query)) || (d.tag && d.tag.some(t => t.toLowerCase().includes(query)));
    }
    return activeCat === "全部" || (d.class && d.class.includes(activeCat));
  });

  const selectedItem = data.find(d => d.id === selectedId);

  if (loading) return <div className="loading-overlay">档案库检索中...</div>;

  return (
    <div className={`app-container view-${mobileViewState}`}>
      
      {/* 1. SIDEBAR */}
      <aside className="sidebar">
        <div className="brand"><h1>运诚科技</h1><p>Institutional Archive</p></div>
        <ul className="cat-nav">
          {categories.map(cat => (
            <li key={cat} className={`cat-item ${activeCat === cat ? 'active' : ''}`} onClick={() => handleSelectCategory(cat)}>
              <span>{cat}</span>
              <span className="count-badge">{data.filter(d => cat === "全部" || (d.class && d.class.includes(cat))).length}</span>
            </li>
          ))}
        </ul>
      </aside>

      {/* 2. LIST VIEW */}
      <section className="list-view">
        <header className="mobile-header">
          <button className="back-btn" onClick={() => setMobileViewState('nav')}>← 档案库</button>
          <div className="mobile-title">{activeCat}</div>
        </header>
        <div className="search-bar">
          <input type="text" placeholder="检索证书名称..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="scroll-list">
          {filtered.map(item => (
            <article key={item.id} className={`list-item ${selectedId === item.id ? 'selected' : ''}`} onClick={() => handleSelectItem(item.id)}>
              <div className="item-meta"><span>ID: {item.id}</span><span>{item.class?.[0]}</span></div>
              <h3>{item.name}</h3>
            </article>
          ))}
        </div>
      </section>

      {/* 3. DETAIL VIEW */}
      <main className="detail-view">
        {selectedItem ? (
          <>
            <header className="mobile-header">
              <button className="back-btn" onClick={() => setMobileViewState('list')}>← 列表</button>
              <div className="mobile-title">档案详情</div>
            </header>
            <div className="detail-content" key={selectedItem.id}>
              <span className="detail-tag">{selectedItem.class?.join(' / ')}</span>
              <h2 className="detail-title">{selectedItem.name}</h2>
              
              <div className="image-box" onClick={() => setIsZoomed(true)} style={{cursor: 'zoom-in'}}>
                <img src={`${API_BASE}/assets/${selectedItem.image}?width=1200`} alt={selectedItem.name} />
                <div className="zoom-hint">点击查看大图</div>
              </div>

              <div className="info-box">
                <h4>档案文本摘要</h4>
                <div className="summary-text">
                  名称: {selectedItem.name}<br/>
                  证号: {selectedItem.no || '见原件'}<br/>
                  标签: {selectedItem.tag?.join(', ') || '无'}
                </div>
              </div>
            </div>

            {/* LIGHTBOX OVERLAY */}
            {isZoomed && (
              <div className="lightbox-overlay" onClick={() => setIsZoomed(false)}>
                <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                  <img src={`${API_BASE}/assets/${selectedItem.image}`} alt="Full Resolution" />
                  <button className="close-zoom" onClick={() => setIsZoomed(false)}>×</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">请选择档案查阅</div>
        )}
      </main>
    </div>
  );
}

export default App;
